'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import Picture from '../../components/picture.js'
import { Text } from '../../components/text.js'

import type { FreeGift, FreeGiftWithSelection } from './types.js'

export interface FreeGiftItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** 赠品数据（包含选择状态） */
  gift: FreeGiftWithSelection
  /** 点击回调 */
  onClick?: (gift: FreeGift) => void
  /** 赠品类型 1-> 单选模式， 2 -> 全选模式 */
  giftType?: 1 | 2
}

/**
 * FreeGiftItem - 免费赠品项
 */
const FreeGiftItem = React.forwardRef<HTMLDivElement, FreeGiftItemProps>(
  ({ gift, onClick, className, giftType }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'bg-container-primary laptop:rounded-[8px] border-lines relative flex w-full flex-col overflow-hidden rounded-[6px] border-2 transition-all',

          // 样式根据 gift_type 和选择状态变化
          giftType === 2
            ? 'border-brand-0 bg-container-secondary-0 cursor-default' // 全选模式：始终选中，不可点击
            : `cursor-pointer hover:shadow-sm ${gift.selected ? 'border-brand-0 bg-container-secondary-0' : 'border-lines bg-secondary-0'}`, // 单选模式：可点击，根据选择状态变化边框
          className
        )}
        onClick={() => {
          // 只有单选模式才允许点击
          if (giftType === 1) {
            onClick?.(gift)
          }
        }}
      >
        <div className="flex size-full flex-col">
          <div className="tablet:h-[24px] lg-desktop:h-[28px] flex h-[18px] justify-end">
            {gift.tag && (
              <div className="bg-brand-0 lg-desktop:text-[16px] laptop:rounded-bl-[8px] rounded-bl-[6px] px-[6px] pb-[4px] pt-[3px] text-[14px] font-bold text-white">
                {gift.tag}
              </div>
            )}
          </div>
          <div className={cn('laptop:pb-4 laptop:px-4 flex size-full items-center gap-4 px-2 pb-2')}>
            <div className={cn('size-14 shrink-0', 'tablet:size-12')}>
              <Picture source={gift.image} alt={gift.name} className="size-full rounded object-cover" />
            </div>
            <div className="flex h-full min-w-0 flex-1 flex-col justify-center gap-1 py-1">
              <Text
                size={2}
                html={gift.name}
                className={cn('text-info-primary desktop:text-[16px] lg-desktop:text-[18px] line-clamp-1')}
              />
              <div className="mt-auto flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Text size={2} className={cn('text-info-primary desktop:text-[16px] lg-desktop:text-[18px]')}>
                    {gift.currentPrice}
                  </Text>
                  {gift.originalPrice && gift.originalPrice !== gift.currentPrice && (
                    <Text
                      size={2}
                      className={cn('text-info-tertiary desktop:text-[16px] lg-desktop:text-[18px] pr-6 line-through')}
                    >
                      {gift.originalPrice}
                    </Text>
                  )}
                </div>
                {gift.quantity >= 1 && (
                  <Text
                    size={4}
                    className={cn('text-info-primary laptop:text-[16px] lg-desktop:text-[18px] self-end text-[14px]')}
                  >
                    x{gift.quantity}
                  </Text>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

FreeGiftItem.displayName = 'FreeGiftItem'

export default FreeGiftItem
